import React, {useEffect, useState} from 'react';
import {Container, Col, Row, Button} from 'reactstrap';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import useBalanceText from 'utils/useBalanceText';
import useBeacon from 'utils/useBeacon';
import track from 'utils/track';

import PageMeta from 'components/PageMeta';
import Header from 'components/Elements/Header';
import Section from 'components/Elements/Section';
import Webinar from 'components/Webinar';
import Video from 'components/Video';
import SalesCopy from 'components/SalesCopy';
import RegistrationModal from 'components/RegistrationModal';
import ModalExitScreening from 'components/Modals/ModalExitScreening';
import ShareButtons from 'components/Social/ShareButtons';
import Footer from 'components/Elements/Footer';

const COMPLETED_MASTERCLASS_EVENT = 'Completed Watching TT2 Masterclass';

export default function ScreeningIndex({replayOverride = false, ...props}) {
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before'
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();
  const {bucket, events = []} = useUserState();
  const [anchorScrolled, setAnchorScrolled] = useState(false);
  const hasCompletedMasterclass =
    events.includes(COMPLETED_MASTERCLASS_EVENT) ||
    webinarSchedule.phase === 'replay' ||
    webinarSchedule.phase === 'after';

  const productID = 'C-TT2';
  const productSku = 'C-TT2';
  const productName = 'Tackling Type 2';
  const productSlug = 'tt2-masterclass';
  const productValue = 247;
  const paymentPlanPrice = 89;

  useEffect(() => {
    if (hasCompletedMasterclass)
      track('Product Viewed', {
        product_id: productID,
        sku: productSku,
        name: productName,
        variant: productSlug,
        value: productValue,
        currency: 'usd'
      });
  }, [hasCompletedMasterclass]);

  useEffect(() => {
    if (anchorScrolled) return;
    if (events.includes(COMPLETED_MASTERCLASS_EVENT)) {
      setAnchorScrolled(true);
      if (window !== undefined && window.location.hash === '#join') {
        document.getElementById('watch-cta-thankyou').scrollIntoView();
      }
    }
  }, [events, anchorScrolled]);

  const webinarHeading = (
    <Row>
      <Col>
        <h4 className="section-heading text-center mb-1">Tackling Type 2 Masterclass</h4>
        <h5 className="mt-0 text-400 text-center">with Brenda Davis, RD, & Ocean Robbins</h5>
        <h2 className="section-heading text-center">4 Diabetes Myths & 10 Groundbreaking Truths</h2>
      </Col>
    </Row>
  );

  const webinarFooter = (
    <Row>
      <Col>
        <h4 className="text-center text-600 mb-0">
          <a
            href="https://cdn.foodrevolution.org/members/pdf/tt2/Tackling%20Type%202%20Masterclass%20Workbook.pdf"
            className="workbook-download text-underline"
            target="_blank"
          >
            Download your FREE workbook here.
          </a>
        </h4>
        <p className="text-center balance-text mt-4 m-0">
          IMPORTANT:{' '}
          {
            {
              prevent: (
                <>Taking the time to learn how to prevent diabetes is the most valuable thing you can do today.</>
              ),
              optimize: (
                <>Taking the time to learn how to end prediabetes is the most valuable thing you can do today.</>
              ),
              reverse: (
                <>
                  Taking the time to learn how to reverse type 2 diabetes is the most valuable thing you can do today.
                </>
              )
            }[bucket]
          }
        </p>
      </Col>
    </Row>
  );

  useBalanceText();
  useBeacon();
  return (
    <div className="page d-flex flex-column">
      <PageMeta title="Watch the Tackling Type 2 Masterclass with Brenda Davis, RD" />

      <div className="page-content">
        <Header bgColor="black" lockupWhite />

        <Section
          id="screening-header-cta"
          color="white"
          className="section-angle-first"
          angle={(hasCompletedMasterclass && !replayOverride) || replayOverride ? 'bottom' : 'none'}
        >
          <Container>
            {replayOverride ? (
              <>
                {webinarHeading}
                <Row className="d-flex justify-content-center">
                  <Col lg="10" className="video-col px-0 px-sm-3">
                    <Video
                      url="https://customer-ros8xhsmbkksflna.cloudflarestream.com/7bb0d442ebebb3f8e246326fa766a24c/watch"
                      label="TT2 - Masterclass"
                    />
                  </Col>
                </Row>
                {webinarFooter}
              </>
            ) : (
              <>
                <Webinar
                  noun="Masterclass"
                  headingSection={webinarHeading}
                  footerSection={webinarFooter}
                  label="TT2 - Masterclass"
                />
              </>
            )}
          </Container>
        </Section>

        {hasCompletedMasterclass && !replayOverride ? (
          <>
            <SalesCopy
              version="screening"
              productId={productID}
              productSku={productSku}
              productSlug={productSlug}
              productPrice={productValue}
              paymentPlanPrice={paymentPlanPrice}
            />
          </>
        ) : null}
      </div>

      {replayOverride ? (
        <Section
          id="replay-cta"
          color="purple"
          innerClass="text-center text-white section-webinar-inner-cta pb-5"
          angle="none"
        >
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading">
                  Are you interested in enrolling in the <i>Tackling Type 2</i> course with Brenda Davis, RD?
                </h4>
                <h5>Click below to learn more:</h5>
                <Button className="btn-join btn-lg" color="cta" href="/join/" target="_blank">
                  Join Now!
                </Button>
              </Col>
            </Row>
          </Container>
        </Section>
      ) : (
        <>
          <RegistrationModal />
          {hasCompletedMasterclass && !replayOverride ? <ModalExitScreening /> : null}
        </>
      )}

      <div className="footer-content mt-auto">
        <Footer />
        <ShareButtons floating />
      </div>
    </div>
  );
}
